/**
 * Debug Configuration
 *
 * Centralized control for console logging verbosity across the application.
 * Reduces console noise while maintaining debugging capability.
 */

// Debug flags - set to true to enable specific categories
const DEBUG_FLAGS = {
  // Core Services
  TAXONOMY: false, // Taxonomy service operations
  CACHE: false, // Cache hits and operations
  API: false, // API requests and responses

  // AI and Metadata
  AI_METADATA: true, // AI metadata generation (keep for now)
  SONG_METADATA: true, // Song metadata processing (keep for debugging)

  // UI Components
  FORM_UPDATES: false, // Form value changes
  COMPONENT_LIFECYCLE: false, // Component mount/unmount

  // Environment
  ENVIRONMENT: false, // Environment detection
  URL_NORMALIZATION: false, // URL normalization operations

  // Search and Assets
  SEARCH: false, // Search operations
  ASSETS: false, // Asset operations

  // Analytics
  HELP_TEXT: false, // Help text analytics
  METRICS: false, // Performance metrics
};

// Override all flags in production
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const FORCE_DEBUG = process.env.REACT_APP_FORCE_DEBUG === 'true';

// Safe environment check
const isProductionEnvironment = () => {
  try {
    return IS_PRODUCTION && !FORCE_DEBUG;
  } catch (error) {
    return false; // Fallback to development behavior
  }
};

/**
 * Check if a debug category is enabled
 */
export const isDebugEnabled = (category: keyof typeof DEBUG_FLAGS): boolean => {
  if (isProductionEnvironment()) {
    return false;
  }
  return DEBUG_FLAGS[category] ?? false;
};

/**
 * Conditional console.log wrapper
 */
export const debugLog = (
  category: keyof typeof DEBUG_FLAGS,
  ...args: any[]
): void => {
  if (isDebugEnabled(category)) {
    console.log(...args);
  }
};

/**
 * Conditional console.warn wrapper
 */
export const debugWarn = (
  category: keyof typeof DEBUG_FLAGS,
  ...args: any[]
): void => {
  if (isDebugEnabled(category)) {
    console.warn(...args);
  }
};

/**
 * Conditional console.error wrapper (always shows in development)
 */
export const debugError = (...args: any[]): void => {
  if (!isProductionEnvironment()) {
    console.error(...args);
  }
};

/**
 * Get current debug configuration
 */
export const getDebugConfig = () => ({
  flags: DEBUG_FLAGS,
  isProduction: IS_PRODUCTION,
  forceDebug: FORCE_DEBUG,
  enabledCategories: Object.entries(DEBUG_FLAGS)
    .filter(([_, enabled]) => enabled)
    .map(([category]) => category),
});

// Export individual flag checkers for convenience
export const debugFlags = DEBUG_FLAGS;
